import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tab, Tabs } from "react-bootstrap";
import { Localization, SessionContext, SettingsManager, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { KeyValueTable } from "src/propertyInfoTemplates/PropertyComponents";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { createDefaultPLAProject, getMyPLAAccessRights, notifyPLAProjectsChanged, PLAProject, PLAUserAccessRights, PLAValueType } from "./PLAState";
import { PLAProjectEditor } from "src/modules/PLAModule/PLAProjectEditor";
import { PLAProjectServiceImpl } from "./PLAPersistence";
import { validatePLAExpression } from "./PLAExpression";

type Props = {
   managedWindow: ManagedWindow;
   onFormClose: () => void;
};

type PLAProjectDefinitions = Pick<PLAProject, "settings" | "elementTypes" | "relationTypes">;
type PendingDefinitionImport = { project: PLAProject; definitions: PLAProjectDefinitions };

const PLAProjectDefinitionsFileType = "PLAProjectDefinitions";
const PLAProjectDefinitionsFileVersion = 1;
const PLAValueTypes: PLAValueType[] = ["string", "number", "date", "boolean"];

function isRecord(value: unknown): value is Record<string, any> {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function downloadBlob(fileName: string, blob: Blob) {
   const url = URL.createObjectURL(blob);
   const link = document.createElement("a");
   link.className = "mit-download-link";
   link.download = fileName;
   link.href = url;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);
}

function safeFileName(value: string) {
   return (value || "pla-project").replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "") || "pla-project";
}

function createDefinitionsExport(project: PLAProject) {
   return {
      fileType: PLAProjectDefinitionsFileType,
      version: PLAProjectDefinitionsFileVersion,
      exportedAt: new Date().toISOString(),
      projectId: project.id,
      projectName: project.name,
      definitions: {
         settings: project.settings || {},
         elementTypes: project.elementTypes || {},
         relationTypes: project.relationTypes || {}
      } satisfies PLAProjectDefinitions
   };
}

function validateProjectDefinitionsFile(value: unknown, targetProject: PLAProject): { definitions?: PLAProjectDefinitions; errors: string[] } {
   const errors: string[] = [];
   if (!isRecord(value)) return { errors: [Localization.getText("Invalid PLA project definitions file")] };
   if (value.fileType !== PLAProjectDefinitionsFileType) errors.push(Localization.getText("Invalid PLA project definitions file"));
   if (value.version !== PLAProjectDefinitionsFileVersion) errors.push(Localization.getText("Unsupported PLA project definitions file version"));
   if (!isRecord(value.definitions)) errors.push(Localization.getText("Missing project definitions"));
   if (errors.length > 0 || !isRecord(value.definitions)) return { errors };

   const definitions = value.definitions as Partial<PLAProjectDefinitions>;
   if (!isRecord(definitions.settings)) errors.push(Localization.getText("Project settings must be an object"));
   if (!isRecord(definitions.elementTypes)) errors.push(Localization.getText("Element types must be an object"));
   if (!isRecord(definitions.relationTypes)) errors.push(Localization.getText("Relation types must be an object"));
   if (errors.length > 0 || !isRecord(definitions.settings) || !isRecord(definitions.elementTypes) || !isRecord(definitions.relationTypes)) return { errors };

   for (const [relationTypeId, relationType] of Object.entries(definitions.relationTypes)) {
      if (!relationTypeId.trim()) errors.push(Localization.getText("Relation type id is required"));
      if (!isRecord(relationType) || typeof relationType.name !== "string") errors.push(`${Localization.getText("Invalid relation type")}: ${relationTypeId}`);
   }

   const validationProject: PLAProject = {
      ...targetProject,
      settings: definitions.settings,
      elementTypes: definitions.elementTypes,
      relationTypes: definitions.relationTypes
   };

   for (const [elementTypeId, elementType] of Object.entries(definitions.elementTypes)) {
      if (!elementTypeId.trim()) errors.push(Localization.getText("Element type id is required"));
      if (!isRecord(elementType)) {
         errors.push(`${Localization.getText("Invalid element type")}: ${elementTypeId}`);
         continue;
      }
      if (typeof elementType.name !== "string") errors.push(`${Localization.getText("Element type name is required")}: ${elementTypeId}`);
      if (typeof elementType.sortOrder !== "number" || !Number.isFinite(elementType.sortOrder)) errors.push(`${Localization.getText("Element type sort order must be a number")}: ${elementTypeId}`);
      if (elementType.color !== undefined && typeof elementType.color !== "string") errors.push(`${Localization.getText("Element type color must be a string")}: ${elementTypeId}`);
      if (!isRecord(elementType.attributes)) {
         errors.push(`${Localization.getText("Element type attributes must be an object")}: ${elementTypeId}`);
         continue;
      }

      for (const [attributeId, attribute] of Object.entries(elementType.attributes)) {
         if (!attributeId.trim()) errors.push(`${Localization.getText("Attribute id is required")}: ${elementTypeId}`);
         if (!isRecord(attribute)) {
            errors.push(`${Localization.getText("Invalid attribute")}: ${elementTypeId}.${attributeId}`);
            continue;
         }
         if (typeof attribute.name !== "string") errors.push(`${Localization.getText("Attribute name is required")}: ${elementTypeId}.${attributeId}`);
         if (attribute.type !== "constant" && attribute.type !== "expression") errors.push(`${Localization.getText("Invalid attribute type")}: ${elementTypeId}.${attributeId}`);
         if (attribute.valueType !== undefined && !PLAValueTypes.includes(attribute.valueType)) errors.push(`${Localization.getText("Invalid value type")}: ${elementTypeId}.${attributeId}`);

         if (attribute.type === "expression") {
            if (typeof attribute.expression !== "string") {
               errors.push(`${Localization.getText("Expression is required")}: ${elementTypeId}.${attributeId}`);
            } else {
               const validation = validatePLAExpression(attribute.expression, { project: validationProject, elementTypeId });
               if (!validation.valid) errors.push(`${Localization.getText("Invalid expression")}: ${elementTypeId}.${attributeId}: ${validation.error}`);
            }
         }

         if (attribute.alerts !== undefined) {
            if (!isRecord(attribute.alerts)) {
               errors.push(`${Localization.getText("Alerts must be an object")}: ${elementTypeId}.${attributeId}`);
            } else {
               for (const [alertId, alert] of Object.entries(attribute.alerts)) {
                  if (!isRecord(alert) || typeof alert.booleanExpression !== "string" || typeof alert.alertMessage !== "string") {
                     errors.push(`${Localization.getText("Invalid alert")}: ${elementTypeId}.${attributeId}.${alertId}`);
                     continue;
                  }
                  const validation = validatePLAExpression(alert.booleanExpression, { project: validationProject, elementTypeId });
                  if (!validation.valid) errors.push(`${Localization.getText("Invalid alert expression")}: ${elementTypeId}.${attributeId}.${alertId}: ${validation.error}`);
               }
            }
         }
      }
   }

   return errors.length > 0
      ? { errors }
      : { definitions: definitions as PLAProjectDefinitions, errors: [] };
}

export function PLAAdminDialog(props: Props) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const [projects, setProjects] = useState<PLAProject[]>([]);
   const [showProjectEditor, setShowProjectEditor] = useState<PLAProject | null>(null);
   const [newProject, setNewProject] = useState(false);
   const [confirmDelete, setConfirmDelete] = useState<string | undefined>();
   const [pendingDefinitionImport, setPendingDefinitionImport] = useState<PendingDefinitionImport | undefined>();

   const projectService = () => new PLAProjectServiceImpl(SettingsManager.getSystemSetting("pla.projectsElasticIndex", ""), sessionState.userName || sessionState.userRef);

   const loadProjects = (preferredProject?: PLAProject) => {
      projectService().loadProjects()
         .then((loadedProjects) => {
            if (!preferredProject) {
               setProjects(loadedProjects);
               return;
            }
            const exists = loadedProjects.some((item) => item.id === preferredProject.id);
            setProjects(exists ? loadedProjects.map((item) => item.id === preferredProject.id ? preferredProject : item) : [...loadedProjects, preferredProject]);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load projects")}: ${error.message || error}`)));
   };

   useEffect(() => {
      loadProjects();
   }, [sessionState.userName]);

   function canAdminProject(project: PLAProject): boolean {
      if (hasAccessToFeature(Feature.PLAToolGlobalAdmin)) return true;
      return getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).includes(PLAUserAccessRights.Admin);
   }

   const handleSaveProject = async (project: PLAProject, createIndex = false) => {
      try {
         const projectsElasticIndex = SettingsManager.getSystemSetting("pla.projectsElasticIndex", "pla_projects_staging");
         const nextProject = { ...project, esIndexName: project.esIndexName || `${projectsElasticIndex}_${project.id}` };
         if (createIndex) {
            await projectService().createProject(nextProject);
         } else {
            await projectService().saveProject(nextProject);
         }
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Project saved")));
         setShowProjectEditor(null);
         setNewProject(false);
         setProjects((prev) => {
            const exists = prev.some((item) => item.id === nextProject.id);
            return exists ? prev.map((item) => item.id === nextProject.id ? nextProject : item) : [...prev, nextProject];
         });
         notifyPLAProjectsChanged();
         loadProjects(nextProject);
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save project")}: ${error.message || error}`));
      }
   };

   const exportProjectDefinitions = (project: PLAProject) => {
      const blob = new Blob([JSON.stringify(createDefinitionsExport(project), null, 2)], { type: "application/json" });
      downloadBlob(`${safeFileName(project.name || project.id)}-pla-definitions.json`, blob);
   };

   const importProjectDefinitions = (project: PLAProject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "application/json,.json";
      input.onchange = async () => {
         const file = input.files?.[0];
         if (!file) return;
         try {
            const parsed = JSON.parse(await file.text());
            const validation = validateProjectDefinitionsFile(parsed, project);
            if (!validation.definitions || validation.errors.length > 0) {
               appMessageDispatch(actionSetErrorMessage(validation.errors.join("\n")));
               return;
            }
            setPendingDefinitionImport({ project, definitions: validation.definitions });
         } catch (error: any) {
            appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to import project definitions")}: ${error.message || error}`));
         }
      };
      input.click();
   };

   const confirmImportProjectDefinitions = () => {
      if (!pendingDefinitionImport) return;
      const nextProject: PLAProject = {
         ...pendingDefinitionImport.project,
         settings: pendingDefinitionImport.definitions.settings,
         elementTypes: pendingDefinitionImport.definitions.elementTypes,
         relationTypes: pendingDefinitionImport.definitions.relationTypes
      };
      setPendingDefinitionImport(undefined);
      handleSaveProject(nextProject);
   };

   return (
      <AdvancedDragModal
         PosDefault={{ width: "1000px", left: "50%", transform: "translateX(-50%)", top: "1rem" }}
         title={Localization.getText("PLA Database administration")}
         onClose={() => windowDispatch(closeWindow(WindowId.PLAAdminDialog))}
         window={props.managedWindow}
      >
         <ADModalBody>
            <Tabs defaultActiveKey="PLAProjects">
               <Tab eventKey="PLAProjects" title={Localization.getText("Projects")}>
                  <ProtectedFeature feature={Feature.PLAToolAdmin}>
                     <KeyValueTable>
                        <tbody>
                           <tr className="bold">
                              <td>{Localization.getText("Project")}</td>
                              <td>{Localization.getText("Protected")}</td>
                              <td>{Localization.getText("ESIndexName")}</td>
                              <td>{Localization.getText("Actions")}</td>
                           </tr>
                           {projects.filter(canAdminProject).map((project) => (
                              <tr key={project.id}>
                                 <td>{project.name}</td>
                                 <td>{project.protected ? Localization.getText("yes") : ""}</td>
                                 <td>{project.esIndexName}</td>
                                 <td>
                                    <GlassButtonRow>
                                       <GlassButton onClick={() => setShowProjectEditor(project)}>{Localization.getText("Edit")}</GlassButton>
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton onClick={() => exportProjectDefinitions(project)}>{Localization.getText("Export definitions")}</GlassButton>}
                                       {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <GlassButton onClick={() => importProjectDefinitions(project)}>{Localization.getText("Import definitions")}</GlassButton>}
                                       {!project.protected && <GlassButton onClick={() => setConfirmDelete(project.id)}>{Localization.getText("Delete")}</GlassButton>}
                                    </GlassButtonRow>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </KeyValueTable>
                     <GlassButtonRow>
                        <GlassButton onClick={() => loadProjects()}>{Localization.getText("Load projects")}</GlassButton>
                        <GlassButton onClick={() => setNewProject(true)}>{Localization.getText("New Project")}</GlassButton>
                     </GlassButtonRow>
                  </ProtectedFeature>
               </Tab>
            </Tabs>
         </ADModalBody>
         {showProjectEditor ? createPortal(
            <PLAProjectEditor value={showProjectEditor} onClose={() => setShowProjectEditor(null)} onChange={(project) => handleSaveProject(project)} />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {newProject ? createPortal(
            <PLAProjectEditor title={Localization.getText("New Project")} value={createDefaultPLAProject(sessionState.userRef, sessionState.customerRef)} onClose={() => setNewProject(false)} onChange={(project) => handleSaveProject(project, true)} />,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {confirmDelete ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Delete Project")}
               onSubmit={() => {
                  projectService().deleteProject(confirmDelete).then(() => loadProjects());
                  setConfirmDelete(undefined);
               }}
               onCancel={() => setConfirmDelete(undefined)}
            >
               {Localization.getText("Confirm deletion")}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
         {pendingDefinitionImport ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Import project definitions")}
               onSubmit={confirmImportProjectDefinitions}
               onCancel={() => setPendingDefinitionImport(undefined)}
            >
               {Localization.getText("Importing project definitions will overwrite the current project definitions. Continue?")}
            </ConfirmationDialog>,
            document.getElementById("Mit-NonBlockingOverlay") || document.body
         ) : null}
      </AdvancedDragModal>
   );
}
